import { cloneElement, isValidElement } from 'react'
import type { ReactNode } from 'react'
import { cx } from '../../cx'
import styles from './FormField.module.css'

export interface FormFieldProps {
  label: ReactNode
  /** Id kontrolki; FormField nadaje je pojedynczemu dziecku, jeśli go nie ma. */
  htmlFor: string
  error?: string
  hint?: string
  required?: boolean
  /** Akcja po prawej stronie etykiety, np. „Nie pamiętasz hasła?" (spec 06). */
  action?: ReactNode
  children: ReactNode
  className?: string
}

export function FormField({
  label,
  htmlFor,
  error,
  hint,
  required = false,
  action,
  children,
  className,
}: FormFieldProps) {
  const hintId = hint !== undefined ? `${htmlFor}-hint` : undefined
  const errorId = error !== undefined ? `${htmlFor}-error` : undefined

  let control: ReactNode = children
  if (isValidElement<Record<string, unknown>>(children)) {
    const childProps = children.props
    const externalDescribedBy =
      typeof childProps['aria-describedby'] === 'string'
        ? childProps['aria-describedby']
        : undefined
    const describedBy = cx(externalDescribedBy, hintId, errorId)
    control = cloneElement(children, {
      id: typeof childProps['id'] === 'string' ? childProps['id'] : htmlFor,
      'aria-describedby': describedBy === '' ? undefined : describedBy,
      'aria-invalid': error !== undefined ? true : childProps['aria-invalid'],
    })
  }

  return (
    <div className={cx(styles.field, className)}>
      <div className={styles.labelRow}>
        <label className={styles.label} htmlFor={htmlFor}>
          {label}
          {required ? (
            <span className={styles.required} aria-hidden="true">
              *
            </span>
          ) : null}
        </label>
        {/* Akcja stoi OBOK etykiety, nie w niej — link wewnątrz <label>
            przejmowałby kliknięcia i psuł nazwę dostępną kontrolki. */}
        {action}
      </div>
      {control}
      {hint !== undefined ? (
        <p id={hintId} className={styles.hint}>
          {hint}
        </p>
      ) : null}
      {error !== undefined ? (
        <p id={errorId} className={styles.error} role="alert">
          {error}
        </p>
      ) : null}
    </div>
  )
}
